import { motion, AnimatePresence } from 'framer-motion';
import { useState, useEffect } from 'react';
import { Smartphone, X } from 'lucide-react';
import { showBetaBadge } from '@/config/release';

interface MobileAppBannerProps {
  variant?: 'landing' | 'coach';
}

export const MobileAppBanner = ({
  variant: _variant = 'landing',
}: MobileAppBannerProps) => {
  // Hide during beta — will bring back with improved UX for GA
  const [isVisible, setIsVisible] = useState(!showBetaBadge);

  useEffect(() => {
    const dismissed = localStorage.getItem('fiveai_mobile_banner_dismissed');
    if (dismissed) setIsVisible(false);
  }, []);

  const handleDismiss = () => {
    setIsVisible(false);
    localStorage.setItem('fiveai_mobile_banner_dismissed', 'true');
  };

  if (!isVisible) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 40 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="fixed bottom-4 left-3 right-3 sm:left-auto sm:right-4 sm:max-w-sm z-50"
      >
        <div className="relative rounded-2xl bg-gradient-to-r from-violet-600 to-indigo-600 p-3.5 shadow-xl shadow-violet-500/20 ring-1 ring-white/10">
          {/* Close button */}
          <button
            onClick={handleDismiss}
            className="absolute -top-2 -right-2 flex h-6 w-6 items-center justify-center rounded-full bg-gray-900 text-white/70 ring-1 ring-white/20 hover:text-white transition-colors"
            aria-label="Dismiss"
          >
            <X className="h-3.5 w-3.5" />
          </button>

          <div className="flex items-center gap-3">
            {/* Icon */}
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/15 backdrop-blur-sm">
              <Smartphone className="h-5 w-5 text-white" />
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-white leading-tight">
                Try Five AI on mobile
              </p>
              <p className="text-xs text-white/70 leading-snug mt-0.5">
                Add to home screen for the best experience
              </p>
            </div>

            {/* CTA */}
            <button
              onClick={() => {
                // PWA install prompt or instructions
                if ('standalone' in navigator) {
                  alert('Tap the share button in your browser, then "Add to Home Screen"');
                }
                handleDismiss();
              }}
              className="shrink-0 rounded-lg bg-white px-3 py-1.5 text-xs font-bold text-violet-700 shadow-sm hover:bg-white/90 transition-colors"
            >
              Install
            </button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};
